import { Link } from "react-router-dom";
import { Database, ChevronRight, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/DashboardLayout";
import { useScrudManifest } from "./useScrudManifest";

export default function DataTables() {
    const { tables, error, loading } = useScrudManifest();

    return (
        <>
            <PageHeader
                eyebrow="Admin · SCRUD"
                title="Data tables"
                description={
                    loading
                        ? "Loading the table registry…"
                        : error
                          ? "The table registry could not be loaded."
                          : `Search, create, read, update and delete records across all ${tables.length} registered application tables. Read-only tables are live views of the app's own collections.`
                }
                testid="datatables-header"
            />

            <div className="px-6 lg:px-12 py-8">
                {error ? (
                    <div className="flex items-start gap-3 rounded-2xl bg-white shadow-soft p-6 text-navy-700">
                        <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-medium">Couldn't reach the admin API.</div>
                            <div className="text-xs text-navy-500 mt-1">
                                The table list comes from the server, so nothing can be
                                shown until it responds.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        data-testid="scrud-index-grid"
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    >
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => (
                                  <div
                                      key={i}
                                      className="rounded-2xl bg-white shadow-soft p-6 h-[168px] animate-pulse"
                                  />
                              ))
                            : tables.map((t) => (
                                  <Link
                                      key={t.routePath}
                                      to={`/admin/data/${t.routePath}`}
                                      data-testid={`scrud-index-${t.routePath}`}
                                      className="group rounded-2xl bg-white shadow-soft p-6 hover:shadow-lift transition-[transform,box-shadow] duration-300 hover:-translate-y-1"
                                  >
                                      <div className="flex items-start justify-between gap-3 mb-4">
                                          <div className="h-10 w-10 rounded-lg bg-gold-500/10 text-gold-700 flex items-center justify-center">
                                              <Database size={18} strokeWidth={1.7} />
                                          </div>
                                          <ChevronRight
                                              size={18}
                                              className="text-navy-500 group-hover:text-gold-600 transition-colors"
                                          />
                                      </div>
                                      <div className="font-display text-lg text-navy-700 mb-1 leading-tight">
                                          {t.title}
                                      </div>
                                      <div className="text-xs text-navy-500 font-mono truncate mb-3">
                                          {t.sourceCollection || `scrud__${t.routePath}`}
                                      </div>
                                      <div className="flex items-center gap-3 text-xs text-navy-500 flex-wrap">
                                          <span>{t.columns.length} cols</span>
                                          <span className="h-1 w-1 rounded-full bg-navy-500/40" />
                                          <span>PK · {t.primaryKey}</span>
                                          {t.readOnly && (
                                              <span className="ml-auto inline-flex items-center rounded-full bg-navy-700 text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5">
                                                  Read-only
                                              </span>
                                          )}
                                      </div>
                                  </Link>
                              ))}
                    </div>
                )}
            </div>
        </>
    );
}
